import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { getReferenceString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { useCallback, useMemo, useState } from 'react';
import { alert } from '@/services/alert';
import { fetchBotWrite } from '@/services/botCalls';
import { showUndoToast } from '@/services/undoToast';
import { LedgerRowActions } from './ledgerColumns';
import { ConnectCandidate } from './ledgerConnect';
import { LedgerRow } from './ledgerRows';

/**
 * Finance's two edits on a ledger row, through the bot (BAR-3032). A saved
 * note shows at once; a connection re-reads the ledger, because an attach can
 * change the row's order, its Type and its neighbours' balances.
 */

const BOT_NAME = 'create-treatment-payment';

const rowKey = (row: LedgerRow): string => `${row.record}:${row.id}`;
const message = (error: unknown): string => (error instanceof Error ? error.message : 'unknown error');

export function useLedgerRowActions(reload: () => void): {
  actions: LedgerRowActions;
  withSavedNotes: (rows: LedgerRow[]) => LedgerRow[];
} {
  const medplum = useMedplum();
  const [savedNotes, setSavedNotes] = useState<Map<string, string | null>>(new Map());

  const onSaveNote = useCallback(
    async (row: LedgerRow, note: string | null): Promise<void> => {
      await fetchBotWrite(
        medplum,
        BOT_NAME,
        { operation: 'setLedgerNote', record: row.record, entryId: row.id, note },
        reload
      );
      setSavedNotes((current) => new Map(current).set(rowKey(row), note));
    },
    [medplum, reload]
  );

  const disconnect = useCallback(
    async (row: LedgerRow): Promise<void> => {
      await fetchBotWrite(
        medplum,
        BOT_NAME,
        { operation: 'disconnectLedgerPatient', record: row.record, entryId: row.id },
        reload
      );
      reload();
    },
    [medplum, reload]
  );

  const connect = useCallback(
    async (row: LedgerRow, patientId: string): Promise<{ connected: string; orderStatus: string | null }> => {
      const { connection } = await fetchBotWrite<{ connection: { connected: string; orderStatus: string | null } }>(
        medplum,
        BOT_NAME,
        {
          operation: 'connectLedgerPatient',
          record: row.record,
          entryId: row.id,
          kind: row.kind,
          patient: { reference: getReferenceString({ resourceType: 'Patient', id: patientId }) },
        },
        reload
      );
      reload();
      return connection;
    },
    [medplum, reload]
  );

  const onConnect = useCallback(
    (row: LedgerRow, candidate: ConnectCandidate): void => {
      const name = formatGivenFamilyName(candidate.patient.name?.[0]);
      connect(row, candidate.patient.id as string)
        .then((connection) => {
          // Paying an order off can't be taken back yet (BAR-3049), so no Undo.
          const paidOff = connection.connected === 'order' && connection.orderStatus === 'PAID';
          showUndoToast(
            `Connected to ${name}`,
            paidOff
              ? undefined
              : () => disconnect(row).catch((error: unknown) => alert.error(`Couldn't undo: ${message(error)}`))
          );
        })
        .catch((error: unknown) => alert.error(`Couldn't connect: ${message(error)}`));
    },
    [connect, disconnect]
  );

  const onDisconnect = useCallback(
    (row: LedgerRow): void => {
      const patientId = row.medplumPatientId;
      disconnect(row)
        .then(() =>
          showUndoToast(
            `Disconnected from ${row.patientDisplayName ?? 'patient'}`,
            patientId
              ? () => connect(row, patientId).catch((error: unknown) => alert.error(`Couldn't undo: ${message(error)}`))
              : undefined
          )
        )
        .catch((error: unknown) => alert.error(`Couldn't disconnect: ${message(error)}`));
    },
    [connect, disconnect]
  );

  const actions = useMemo(() => ({ onSaveNote, onConnect, onDisconnect }), [onSaveNote, onConnect, onDisconnect]);

  const withSavedNotes = useCallback(
    (rows: LedgerRow[]): LedgerRow[] =>
      savedNotes.size === 0
        ? rows
        : rows.map((row) =>
            savedNotes.has(rowKey(row)) ? { ...row, note: savedNotes.get(rowKey(row)) ?? null } : row
          ),
    [savedNotes]
  );

  return { actions, withSavedNotes };
}
